import { useState } from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Skeleton, useTheme } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

import { usePagination } from '@frontend/features/common/components/Table';
import { Link } from '@frontend/features/common/components/Link';
import { ListLayout } from '@frontend/features/common/components/ListLayout';
import { TABLE_HEIGHT_WITH_AGENCIES_LAYOUT } from '@frontend/features/agencies/constants';

import { routes } from '../routes';
import { useGetInvalidationRulesByAgency } from '@frontend/features/invalidation-rules/queries';
import { formatISOStringToFriendlyDate, getTimeSinceDate } from '@frontend/features/common/helper';
import { IconAvatar } from '@frontend/features/common/components/IconAvatar';
import { Person } from '@mui/icons-material';
import { ListContentLayout } from '@frontend/features/common/components/ListContentLayout';
import {ServiceTypeIcon} from '@frontend/features/invalidation-rules/components/ServiceTypeIcon';
import {CREATE_INVALIDATION_RULE} from '@frontend/features/invalidation-rules/constants';

export const InvalidationRulesList = () => {
  const { t } = useTranslation();
  const { id: agencyId } = useParams();
  const theme = useTheme();
  const pagination = usePagination();
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const { data, isLoading } = useGetInvalidationRulesByAgency({
    pageSize: pagination.rowsPerPage,
    currentPage: pagination.page,
    search: search,
    agencyId: Number(agencyId),
  });

  const handleOnSearch = (value: string) => setSearch(value);
  
  const goToFormView = (ruleId:string) => {
    agencyId && navigate(
      `${routes.dashboard.agencyView.root.replace(':id', agencyId)}/${routes.dashboard.agencyView.invalidationRule.replace(':invalidationRuleId', ruleId)
      }`
    );
  };
  
  const handleOnCreate = () => goToFormView(CREATE_INVALIDATION_RULE);
  

  return (
    <Box sx={{ overflowX: 'hidden', width: '100%' }}>
      <ListLayout padding={0} title={t('invalidationRules.title')} subtitle={t('invalidationRules.subtitle')} >
        <ListContentLayout
          height={TABLE_HEIGHT_WITH_AGENCIES_LAYOUT}
          stickyHeader
          pagination={pagination}
          onSearchChange={handleOnSearch}
          onCreate={handleOnCreate}
          columns={[
            {
              id: 'name',
              label: t('invalidationRules.name'),
              align: 'left',
              minWidth:150,
              isSticky: true,
            },
            {
              id: 'expireAfter',
              label: t('invalidationRules.expireAfter'),
              align: 'left',
              isSticky: true,
            },
            {
              id: 'order',
              label: t('invalidationRules.table.order'),
              align: 'left',
              isSticky: true,
              minWidth:20,
              maxWidth:50
            },
            {
              id: 'isActive',
              label: t('invalidationRules.isActive'),
              align: 'left',
              minWidth:20,
              maxWidth:50
            },
            {
              id: 'isDefault',
              label: t('invalidationRules.table.isDefault'),
              align: 'left',
              minWidth:20,
              maxWidth:50
            },
            {
              id: 'serviceType',
              label: t('invalidationRules.table.serviceType'),
              align: 'left',
            },

            {
              id: 'createdAt',
              sortField: 'createdAt',
              label: t('invalidationRules.table.created'),
              align: 'left',
            },
            {
              id: 'createdBy',
              label: t('invalidationRules.table.createdBy'),
              align: 'left',
              maxWidth:200
            },
            {
              id: 'updatedSince',
              label: t('invalidationRules.table.lastUpdate'),
              align: 'left',
            },
            {
              id: 'updatedBy',
              label: t('invalidationRules.table.updatedBy'),
              align: 'left',
              maxWidth:200
            },
            { id: 'actions', label: '', align: 'left' },
          ]}
          isLoading={isLoading}
          data={
            data && {
              key: 'id',
              items: data.items.map((item) => ({
                ...item,
                name: (
                  <Link
                    to={`${routes.dashboard.agencyView.root.replace(
                      ':id',
                      String(agencyId),
                    )}/${routes.dashboard.agencyView.invalidationRule.replace(':invalidationRuleId', item.id)}`}
                  >
                    {item.name}
                  </Link>
                ),
                expireAfter: `${item.expireAfter} ${t('invalidationRules.minutes')}`,
                order: item.order,
                isActive: `${item.isActive ? t('invalidationRules.table.yes') : t('invalidationRules.table.no')}`,
                isDefault: `${item.isDefault ? t('invalidationRules.table.yes') : t('invalidationRules.table.no')}`,
                serviceType: <ServiceTypeIcon type={item.serviceType} />,
                createdAt: formatISOStringToFriendlyDate(item.createdAt),
                createdBy: item.createdBy ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <IconAvatar>
                      <Person fontSize="small" htmlColor={theme.palette.common.white} />
                    </IconAvatar>
                    <Link underline="hover" to={routes.dashboard.userView.replace(':id', String(item.createdBy))}>
                      {`${item.createdByFirstName} ${item.createdByLastName}`}
                    </Link>
                  </Box>
                ) : (
                  'N/A'
                ),
                updatedSince: getTimeSinceDate(item.updatedAt),
                updatedBy: item.updatedBy ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <IconAvatar>
                      <Person fontSize="small" htmlColor={theme.palette.common.white} />
                    </IconAvatar>
                    <Link underline="hover" to={routes.dashboard.userView.replace(':id', String(item.updatedBy))}>
                      {`${item.updatedByFirstName} ${item.updatedByLastName}`}
                    </Link>
                  </Box>
                ) : (
                  'N/A'
                ),
                actions: (
                  <IconButton onClick={() => goToFormView(item.id)}>
                    <EditIcon color="primary" />
                  </IconButton>
                ),
              })),
              totalCount: data.count,
            }
          }
        />

        {isLoading && <Skeleton height={theme.spacing(10)} variant="rounded" animation="wave" />}
      </ListLayout>
    </Box>
  );
};
